/**
 * Вкладка «Севооборот» — посев по зонам + история культур (эталон founder v3,
 * 2026-07-11). Модель ADR-049, но слов «leaf/XOR/занять/API» на экране нет.
 *
 * Состояния:
 *  · поле без зон  → «сеем целиком», выбор цели скрыт;
 *  · поле с зонами → «Куда сеем» (Всё поле / зоны) + зелёный «Итог по полю»;
 *  · конфликт      → поле сеется ЛИБО целиком, ЛИБО по зонам: показываем, что
 *                    мешает, и кнопку-переход к мешающему посеву, чтобы его
 *                    снять (в обе стороны — AK-449). Сохранение при конфликте
 *                    заблокировано; база держит то же правило и вернёт 409.
 *
 * PR-C (финал рефактора карточки поля v3). Заменяет per-leaf редакторы PR-A
 * единой формой SowForm по выбранной цели.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { listSeasons } from '@/lib/season/api';
import { isSeasonCompleted } from '@/lib/season/computedStatus';
import { useCropsReference } from '@/lib/map/useCropsReference';
import { useFieldStateLabel } from '@/lib/reference/fieldCatalogs';
import type { FieldDetail } from '@/components/fields/FieldOverviewTab';
import { useCropHistory, useOccupancy } from './api';
import { GrowingGoalChips } from './GrowingGoalChips';
import { SowForm } from './SowForm';
import { CropHistoryTable } from './CropHistoryTable';
import { SowingActions } from './SowingActions';
import { SowWizard } from './sow/SowWizard';
import { SeasonPlanCard } from './plan/SeasonPlanCard';
import { useFieldSeasonPlan } from './plan/api';
import {
  buildSowTargets,
  fieldHasZones,
  findRepeatCandidate,
  fmtHa,
  isWholeFieldTarget,
  leafSowingLabel,
  occupiedZonesSummary,
  sowBlock,
  sowingActionsTitle,
  sownUnitLeaves,
  summarizeField,
  zonesWord,
} from './rotationLogic';

interface Props {
  detail: FieldDetail;
  fieldId: string;
  canWrite: boolean;
}

export function OccupySeasonTab({ detail, fieldId, canWrite }: Props) {
  const seasons = useQuery({
    queryKey: ['seasons', detail.company_id],
    queryFn: () => listSeasons(detail.company_id ?? undefined),
    staleTime: 5 * 60_000,
  });

  const [seasonId, setSeasonId] = useState<string | null>(
    detail.current_season?.id ?? null,
  );
  useEffect(() => {
    if (seasonId) return;
    const cur = seasons.data?.find((s) => s.isCurrent) ?? seasons.data?.[0];
    if (cur) setSeasonId(cur.id);
  }, [seasons.data, seasonId]);

  // Мастер «посеять без плана» (В-2): самый частый вход в посев, когда плана
  // на поле нет. Живёт диалогом поверх вкладки — вкладка остаётся видна, и
  // после сохранения агроном сразу видит появившийся посев.
  const [wizardOpen, setWizardOpen] = useState(false);
  // Мост «бюджет → поле»: тот же мастер, но открытый из план-карточки. Две
  // ветки — «по плану» (форма заполнена бюджетом) и «иначе» (форма пустая,
  // расхождение с программой видно по ходу). Третьей формы не заводим: это
  // прямое требование прототипа В-3 («тот же мастер сева плюс блок причины»).
  const [planMode, setPlanMode] = useState<'by_plan' | 'otherwise' | null>(null);
  const seasonPlan = useFieldSeasonPlan(fieldId, seasonId);

  const occ = useOccupancy(fieldId, seasonId);
  const crops = useCropsReference();
  const history = useCropHistory(fieldId);

  // Подписи вида пара — из справочника reference.field_states.
  const fallowLabel = useFieldStateLabel();

  const view = occ.data;
  const hasZones = fieldHasZones(view);
  const summary = useMemo(
    () => summarizeField(view, fallowLabel),
    [view, fallowLabel],
  );

  // Сколько поля уже занято посевами этого сезона — мастер показывает
  // «свободно N га», чтобы агроном не считал остаток в уме перед вторым
  // посевом («пол-поля одним, пол-поля другим»).
  const occupiedAreaHa = useMemo(() => {
    if (!view) return null;
    const sown = sownUnitLeaves(view.leaves).filter((l) => l.sowing);
    if (!sown.length) return 0;
    return sown.reduce((acc, l) => acc + (l.area_ha ?? 0), 0);
  }, [view]);

  // Список целей «Куда сеем»: при зонах — «Всё поле» + сами зоны; без зон —
  // единственная цель (поле целиком).
  const targets = useMemo(() => buildSowTargets(view), [view]);

  // Цель по умолчанию: первая зона (не «Всё поле» — чтобы не толкать агронома
  // в XOR-конфликт), а без зон — единственный участок, само поле.
  //
  // Считаем ПРИ РЕНДЕРЕ, а не в useEffect после него. Через эффект форма один
  // кадр жила на «Всё поле» и только потом переезжала на зону, а SowForm
  // пересоздаётся по key участка — значит уже выбранная плитка («Оставить под
  // пар») молча сбрасывалась на «Посеять культуру». Тот же эффект срабатывал
  // на каждое обновление раскладки (в т.ч. рефетч после сохранения) и выкидывал
  // агронома с выбранной зоны обратно на первую.
  const defaultSubzoneId = useMemo(() => {
    if (!view) return null;
    if (hasZones) {
      return view.leaves.find((l) => l.subzone_id != null)?.subzone_id ?? null;
    }
    return view.leaves[0]?.subzone_id ?? null;
  }, [view, hasZones]);

  // `null` — это валидный выбор («Всё поле»), поэтому «ещё ничего не выбирали»
  // кодируем `undefined`.
  const [pickedSubzoneId, setPickedSubzoneId] = useState<
    string | null | undefined
  >(undefined);
  // Сменили сезон — раскладка другая, выбор начинаем заново.
  useEffect(() => {
    setPickedSubzoneId(undefined);
  }, [seasonId]);

  // Выбранная зона исчезла из раскладки (зону удалили) → возвращаемся к цели
  // по умолчанию, а не показываем пустоту.
  const pickedExists =
    pickedSubzoneId !== undefined &&
    targets.some((t) => (t.subzone_id ?? null) === pickedSubzoneId);
  const targetSubzoneId = pickedExists
    ? (pickedSubzoneId as string | null)
    : defaultSubzoneId;

  const selectedLeaf =
    targets.find((t) => (t.subzone_id ?? null) === (targetSubzoneId ?? null)) ??
    targets[0];

  const selectedSeason = seasons.data?.find((s) => s.id === seasonId) ?? null;
  const currentSeasonYear = selectedSeason?.year ?? null;
  // Выбранный сезон уже прошёл (канон терминологии v3 §5) — тогда занятие
  // «Запланировано» показывается как «Не реализован». Только показ: статус в
  // БД ведёт автомат от тех.операций (ADR-063).
  const seasonCompleted = selectedSeason
    ? isSeasonCompleted({
        startsOn: selectedSeason.startsOn,
        endsOn: selectedSeason.endsOn,
        year: selectedSeason.year,
        isCurrent: selectedSeason.isCurrent,
      })
    : false;
  const repeatCandidate = findRepeatCandidate(
    history.data,
    targetSubzoneId,
    currentSeasonYear,
  );

  // AK-446 «Создать посев» — ЯКОРЬ к форме планирования ниже, а не вторая
  // модальная форма (вердикт design-lead в wireframe v4: планирование —
  // многополевая обратимая форма, второй её экземпляр = две правды).
  // Фокус переносим по-настоящему, а не только скроллим: иначе клавиатурный и
  // screen-reader-пользователь не поймут, что вообще произошло.
  const sowFormRef = useRef<HTMLDivElement | null>(null);
  const [anchorPulse, setAnchorPulse] = useState(false);
  const focusSowForm = useCallback(() => {
    const node = sowFormRef.current;
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    node
      .querySelector<HTMLElement>(
        'input, select, button, [tabindex]:not([tabindex="-1"])',
      )
      ?.focus({ preventScroll: true });
    setAnchorPulse(true);
    window.setTimeout(() => setAnchorPulse(false), 1500);
  }, []);

  // Конфликт «целиком ⊕ по зонам»: объясняем ДО сохранения и даём путь —
  // перейти к участку с мешающим посевом и снять его (AK-449).
  const block = useMemo(
    () => sowBlock(view, targetSubzoneId),
    [view, targetSubzoneId],
  );

  // AK-549. Сколько зон занято и на какой площади — это показывает строка
  // «Всё поле» вместо кнопки посева целиком.
  const zonesOccupied = useMemo(() => occupiedZonesSummary(view), [view]);

  return (
    <div className="space-y-3 py-3">
      {seasonId && (
        <SowWizard
          // Мастер пересоздаётся при смене ветки: черновик «по плану» и
          // черновик «иначе» — разные стартовые состояния, и переиспользовать
          // один экземпляр значило бы показать поля прошлой ветки.
          key={planMode ?? 'plain'}
          open={wizardOpen}
          onOpenChange={(v) => {
            setWizardOpen(v);
            if (!v) setPlanMode(null);
          }}
          fieldId={fieldId}
          fieldName={detail.name ?? 'Поле'}
          fieldAreaHa={summary.totalAreaHa}
          occupiedHa={occupiedAreaHa}
          seasonId={seasonId}
          isRiceSystem={Boolean(detail.is_rice_system)}
          plan={planMode ? (seasonPlan.data ?? null) : null}
          planMode={planMode ?? 'by_plan'}
        />
      )}

      {/* План на сезон — ПЕРВЫМ блоком вкладки: агроном открывает поле, чтобы
          узнать «что здесь сеем», и ответ холдинга обязан стоять до формы. */}
      <SeasonPlanCard
        fieldId={fieldId}
        seasonId={seasonId}
        canWrite={canWrite}
        onSowByPlan={() => {
          setPlanMode('by_plan');
          setWizardOpen(true);
        }}
        onSowOtherwise={() => {
          setPlanMode('otherwise');
          setWizardOpen(true);
        }}
      />
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">Посевы</CardTitle>
              <p className="text-sm text-muted-foreground">
                Что сеем на поле в этом сезоне. Нет зон — сеем целиком; есть зоны
                (круг, углы, чеки) — выбираем, куда именно.
              </p>
            </div>
            {canWrite && seasonId && (
              <Button size="sm" onClick={() => setWizardOpen(true)}>
                Добавить посев
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-xs space-y-2">
            <Label>Сезон</Label>
            <Select
              value={seasonId ?? ''}
              onValueChange={setSeasonId}
              disabled={seasons.isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Выберите сезон" />
              </SelectTrigger>
              <SelectContent>
                {(seasons.data ?? []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.year}
                    {s.isCurrent ? ' (текущий)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!seasonId ? (
            <Alert>
              <AlertDescription>Выберите сезон, чтобы посеять поле.</AlertDescription>
            </Alert>
          ) : occ.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : occ.isError ? (
            <Alert variant="destructive">
              <AlertDescription>Не удалось загрузить посевы поля.</AlertDescription>
            </Alert>
          ) : view && selectedLeaf ? (
            <div className="space-y-4">
              {!canWrite && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Недостаточно прав для изменения посева — показан только просмотр.
                  </AlertDescription>
                </Alert>
              )}

              {hasZones ? (
                <div className="space-y-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Куда сеем
                  </div>
                  <div className="space-y-2">
                    {targets.map((t) => {
                      const on =
                        (t.subzone_id ?? null) === (targetSubzoneId ?? null);
                      const sownLabel = leafSowingLabel(t, fallowLabel);
                      // AK-549. Причина считается ДЛЯ ЭТОЙ строки, а не для
                      // выбранной цели: раньше строки зон показывали «не
                      // посеяна» и живую кнопку «Создать посев», хотя поле
                      // занято целиком — клик уводил в двойной счёт площади,
                      // а объяснение появлялось только после выбора зоны.
                      const rowBlock = t.sowing
                        ? null
                        : sowBlock(view, t.subzone_id);
                      return (
                        <div
                          key={t.subzone_id ?? 'whole'}
                          className={cn(
                            'flex w-full flex-wrap items-center justify-between gap-2 rounded-xl border px-4 py-3 transition-colors',
                            on
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:border-primary/60',
                          )}
                        >
                          {/* Выбор зоны и действия по её посеву — соседи, а не
                              вложенные кнопки: кнопка внутри кнопки невалидна
                              и на клавиатуре ведёт себя непредсказуемо. */}
                          <button
                            type="button"
                            onClick={() => setPickedSubzoneId(t.subzone_id)}
                            aria-pressed={on}
                            className="min-w-[12rem] flex-1 text-left"
                          >
                            <div className="text-sm font-medium">{t.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {fmtHa(t.area_ha)}
                              {isWholeFieldTarget(t.subzone_id)
                                ? ' · целиком'
                                : sownLabel
                                  ? ` · посеян: ${sownLabel.toLowerCase()}`
                                  : ' · не посеяна'}
                            </div>
                            {t.sowing?.occupancy_type === 'crop' && (
                              <GrowingGoalChips
                                goals={t.sowing.growing_goals}
                                className="mt-1"
                              />
                            )}
                          </button>
                          {/* AK-446, экран Ж: круг посеяли, углы ещё нет —
                              действие обязано стоять в строке своей зоны. */}
                          {!isWholeFieldTarget(t.subzone_id) ? (
                            <SowingActions
                              compact
                              sowing={t.sowing ?? null}
                              title={sowingActionsTitle(t, fallowLabel)}
                              canWrite={canWrite}
                              seasonCompleted={seasonCompleted}
                              createDisabledReason={rowBlock?.reason ?? null}
                              onCreate={() => {
                                setPickedSubzoneId(t.subzone_id);
                                focusSowForm();
                              }}
                            />
                          ) : (
                            /* AK-549. «Всё поле» кнопки посева не получает —
                               сеять целиком и по зонам одновременно нельзя.
                               Вместо пустоты на её месте — куда ушла площадь
                               поля: «Занято по зонам: 3 зоны, 40,0 га». */
                            zonesOccupied && (
                              <span
                                className="text-xs text-muted-foreground"
                                title={rowBlock?.reason ?? undefined}
                              >
                                Занято по зонам: {zonesOccupied.count}{' '}
                                {zonesWord(zonesOccupied.count)}
                                {zonesOccupied.areaHa != null
                                  ? `, ${fmtHa(zonesOccupied.areaHa)}`
                                  : ''}
                              </span>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {block && (
                    <Alert>
                      <TriangleAlert className="h-4 w-4" />
                      <AlertDescription className="space-y-2">
                        <div>{block.reason}</div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPickedSubzoneId(block.gotoSubzoneId)}
                        >
                          {block.gotoLabel}
                        </Button>
                      </AlertDescription>
                    </Alert>
                  )}

                  {summary.sownCount > 0 && (
                    <div className="rounded-lg bg-primary/10 px-3 py-3 text-sm text-primary">
                      Итог по полю: {summary.sownCount}{' '}
                      {summary.sownCount === 1 ? 'посев' : 'посева'} ·{' '}
                      {summary.items
                        .map(
                          (i) =>
                            `${i.label}${i.areaHa != null ? ` ${fmtHa(i.areaHa)}` : ''}`,
                        )
                        .join(' + ')}{' '}
                      · {fmtHa(summary.totalAreaHa)} — считается автоматически
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Всё поле · {fmtHa(selectedLeaf.area_ha)} — сеем целиком (зон нет)
                </p>
              )}

              {/* AK-446 Ш1 — факт-действия по посеву («что уже сделали»)
                  отдельным блоком НАД формой планирования («что решили
                  сеять»). Бейдж статуса переехал внутрь блока: он часть
                  ответа «что сейчас с посевом», а не самостоятельная секция —
                  раньше он висел между формой и полосой и читался как третья
                  сущность. */}
              <SowingActions
                sowing={selectedLeaf.sowing ?? null}
                title={sowingActionsTitle(selectedLeaf, fallowLabel)}
                canWrite={canWrite}
                seasonCompleted={seasonCompleted}
                // AK-549: та же причина, что гасит кнопку в строке зоны —
                // блок действий не должен предлагать то, что сервер отвергнет.
                createDisabledReason={
                  selectedLeaf.sowing ? null : (block?.reason ?? null)
                }
                onCreate={focusSowForm}
              />

              <div
                ref={sowFormRef}
                className={cn(
                  'rounded-xl transition-shadow',
                  anchorPulse && 'ring-2 ring-primary ring-offset-2',
                )}
              >
                <SowForm
                  key={`${seasonId}:${selectedLeaf.subzone_id ?? 'whole'}`}
                  fieldId={fieldId}
                  seasonId={seasonId}
                  leaf={selectedLeaf}
                  crops={crops.data ?? []}
                  canWrite={canWrite}
                  repeatCandidate={repeatCandidate}
                  isRiceSystem={detail.is_rice_system === true}
                  blockedReason={block?.reason ?? null}
                />
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">История культур на поле</CardTitle>
          <p className="text-sm text-muted-foreground">
            Посевы по сезонам — план и факт урожайности, ц/га.
          </p>
        </CardHeader>
        <CardContent>
          <CropHistoryTable
            rows={history.data}
            isLoading={history.isLoading}
            isError={history.isError}
            showZone={hasZones}
          />
        </CardContent>
      </Card>
    </div>
  );
}
